import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useContext, useEffect, useState } from 'react';
import * as yup from 'yup';
import CoursesApi from '../../../apis/CoursesApi';
import ManagementApi from '../../../apis/ManagementApi';
import SnackbarContext from '../../../contexts/SnackbarContext';
import { RequiredTrainingDto } from '../../../generated-types/requiredTrainingDto';
import useApi from '../../../lib/useApi';
import FormikCheckboxGroup from '../../FormikCheckboxGroup/FormikCheckboxGroup';
import LoadingCircle from '../../LoadingCircle/LoadingCircle';
import DataRefreshContext from '../lib/DataRefreshContext';

/**
 * Props interface for EditRequiredTrainingDialog component.
 */
interface EditRequiredTrainingDialogProps {
  /**
   * Determines if the dialog is open.
   */
  open: boolean;
  /**
   * Function to call when the dialog should be closed.
   */
  onClose: () => void;
}

/**
 * EditRequiredTrainingDialog component allows managers to edit the required trainings
 * for their organization. It fetches the current required trainings and available courses,
 * displays them in a form, and allows the manager to update the required trainings.
 */
const EditRequiredTrainingDialog = (props: EditRequiredTrainingDialogProps) => {
  // State to hold the mapping of course IDs to their names
  const [courseIdNameMapping, setCourseIdNameMapping] = useState<
    Record<string, string>
  >({});

  // Access the triggerDataRefresh function from the context
  const { triggerDataRefresh } = useContext(DataRefreshContext);

  // State to hold the initially required training course IDs
  const [initialRequiredTraining, setInitialRequiredTraining] = useState<
    string[]
  >([]);

  // Control loading indicators
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialLoading, setInitialLoading] = useState(false);

  // State to indicate if there was an error during data fetching
  const [hasError, setHasError] = useState(false);

  // Instantiate ManagementApi and CoursesApi using the custom useApi hook
  const managementApi = useApi(ManagementApi);
  const coursesApi = useApi(CoursesApi);

  // Access the SnackbarContext for displaying notifications
  const snackbar = useContext(SnackbarContext);

  /**
   * Yup validation schema for the form.
   */
  const schema = yup.object({
    // An array of strings, each representing a valid course ID.
    courseIds: yup.array().of(yup.string().required()).required(),
  });

  /**
   * useFormik hook to manage form state, validation, and submission.
   */
  const formik = useFormik<RequiredTrainingDto>({
    initialValues: {
      courseIds: initialRequiredTraining,
    },
    validationSchema: schema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      setIsLoading(true); // Set loading state to true during submission
      try {
        // Call the API to update required trainings with the selected course IDs
        await managementApi.UpdateRequiredTrainings(values);

        // Show a success alert in the snackbar
        snackbar.setAlert(
          <Alert variant="filled" severity="success">
            Updated required trainings successfully
          </Alert>
        );
        snackbar.triggerSnackbar(); // Trigger the snackbar to display the alert
        triggerDataRefresh(); // Refresh the data
        props.onClose(); // Close the dialog upon successful submission
      } catch (error) {
        console.error('Failed to update required trainings', error); // Log the error for debugging

        // Show an error alert in the snackbar
        snackbar.setAlert(
          <Alert variant="filled" severity="error">
            Failed to update required trainings
          </Alert>
        );
        snackbar.triggerSnackbar(); // Trigger the snackbar to display the alert
      } finally {
        setIsLoading(false); // Reset loading state after submission
      }
    },
  });

  /**
   * fetchData function fetches the course mappings and current required trainings.
   * It sets the state accordingly and handles any errors that occur during fetching.
   */
  const fetchData = useCallback(async () => {
    setInitialLoading(true); // Set loading state to true during data fetching
    setHasError(false); // Reset error state before attempting to fetch

    try {
      // Fetch course ID-name mapping and current required trainings in parallel
      const [courseIdNameMap, requiredCourseIds] = await Promise.all([
        coursesApi.GetIdNameMapping(),
        managementApi.GetRequiredTrainings(),
      ]);

      setCourseIdNameMapping(courseIdNameMap);
      setInitialRequiredTraining(requiredCourseIds);
    } catch (error) {
      console.error('Failed to fetch courses or required trainings', error);
      setHasError(true);

      // Show an error alert in the snackbar
      snackbar.setAlert(
        <Alert variant="filled" severity="error">
          Failed to fetch courses or required trainings
        </Alert>
      );
      snackbar.triggerSnackbar();
    } finally {
      setInitialLoading(false);
    }
  }, [coursesApi, managementApi, snackbar]);

  /**
   * useEffect hook triggers data fetching when the dialog is opened.
   * It ensures that fresh data is loaded each time the dialog is displayed.
   */
  useEffect(() => {
    if (props.open) {
      fetchData(); // Fetch data when the dialog is opened
    }
  }, [props.open, fetchData]);

  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth>
      <DialogTitle>Edit Required Training</DialogTitle>
      <DialogContent sx={{ minHeight: '10em' }}>
        {hasError ? (
          // Display an error icon and message if there was an error fetching data
          <Box display="flex" flexDirection="column" alignItems="center" mt={2}>
            <ErrorOutlineRoundedIcon color="error" style={{ fontSize: 80 }} />{' '}
            {/* Error icon */}
            <Typography variant="h6" mt={2}>
              Failed to load required trainings
            </Typography>
            <Typography
              variant="body2"
              color="textSecondary"
              align="center"
              mt={1}
            >
              Please try again or contact support
            </Typography>
          </Box>
        ) : (
          // Display the form if data is loaded successfully
          <form onSubmit={formik.handleSubmit}>
            <Box marginBottom="1em">
              <Typography>
                Select the training that should be <strong>required</strong> for
                your organization. Unselected training can still be optionally
                completed by your members.
              </Typography>
            </Box>
            {/* FormikCheckboxGroup renders a group of checkboxes based on the courses */}
            <FormikCheckboxGroup
              formik={formik}
              name="courseIds"
              options={courseIdNameMapping}
            />
            <Box display="flex" justifyContent="flex-end" marginTop="1em">
              {/* Cancel button to close the dialog without saving changes */}
              <LoadingButton
                onClick={props.onClose}
                color="secondary"
                variant="outlined"
                disabled={isLoading}
                sx={{ marginRight: '1em' }}
              >
                Cancel
              </LoadingButton>
              {/* Save button to submit the form */}
              <LoadingButton
                type="submit"
                variant="contained"
                loading={formik.isSubmitting}
                disabled={isLoading}
              >
                Save
              </LoadingButton>
            </Box>
          </form>
        )}
        <LoadingCircle
          isLoading={isInitialLoading}
          hideText
          loadingDelayMs={0}
        />
      </DialogContent>
    </Dialog>
  );
};

export default EditRequiredTrainingDialog;
